import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, onAuthChange, isUserAdmin } from "./firebase";
import "./ParafiaWizytowka.css";
import VerticalMenu from './components/VerticalMenu/VerticalMenu.js';
import NewsSection from "./pages/news/newsSection/NewsSection.js";
import NewsDetails from "./pages/news/newsDetails/NewsDetails.js";
import Contact from "./pages/contact/Contact.js";
import About from "./pages/about/About.js";
import Liturgy from "./pages/liturgy/Liturgy";
import Patron from "./pages/patron/Patron";
import Office from "./pages/office/Office";
import Chapel from "./pages/chapel/Chapel";
import Priests from "./pages/priests/Priests";
import Groups from "./pages/groups/Groups";
import ChildrenProtection from "./pages/protection/ChildrenProtection";
import LoadingSpinner from "./components/LoadingSpinner/LoadingSpinner.js";
import Intentions from "./pages/intentions/Intentions.js";
import Announcements from "./pages/announcements/Announcements.js";
import AdminPanel from "./pages/admin/AdminPanel";
import Login from "./pages/login/Login.js";
import { MdAdminPanelSettings } from 'react-icons/md';
import { fanpage } from "./data/aboutData.js";
import ItemDetails from "./pages/office/details/ItemDetails.js";
import { items, itemDetails } from "./data/officeData.js";

const ParafiaWizytowka = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const parishStatus = "PARAFIA RZYMSKOKATOLICKA";
  const patronName = "ŚWIĘTEJ URSZULI LEDÓCHOWSKIEJ";
  const parishCity = "w Częstochowie";

  const getPageFromPath = (pathname) => {
    switch (pathname) {
      case "/o-parafii": return "O Parafii";
      case "/duszpasterze": return "Duszpasterze";
      case "/grupy-parafialne": return "Grupy parafialne";
      case "/sw-urszula": return "Św. Urszula";
      case "/msze-swiete": return "Msze Święte";
      case "/nabozenstwa": return "Nabożeństwa";
      case "/kancelaria": return "Kancelaria";
      case "/ochrona-dzieci": return "Ochrona dzieci";
      case "/kontakt": return "Kontakt";
      case "/intencje": return "Intencje";
      case "/ogloszenia": return "Ogłoszenia";
      case "/login": return "Login";
      case "/admin": return "Panel administratora";
      default:
        if (pathname.startsWith("/kancelaria/")) {
          return "ItemDetails";
        }

        return "Msze Święte";
    }
  };


  const getPathFromPage = (page) => {
    switch (page) {
      case "O Parafii": return "/o-parafii";
      case "Duszpasterze": return "/duszpasterze";
      case "Grupy parafialne": return "/grupy-parafialne";
      case "Św. Urszula": return "/sw-urszula";
      case "Msze Święte": return "/msze-swiete";
      case "Nabożeństwa": return "/nabozenstwa";
      case "Kancelaria": return "/kancelaria";
      case "Ochrona dzieci": return "/ochrona-dzieci";
      case "Kontakt": return "/kontakt";
      case "Intencje": return "/intencje";
      case "Ogłoszenia": return "/ogloszenia";
      case "Login": return "/login";
      case "Panel administratora": return "/admin";
      default: return "/";
    }
  };

  const [activePage, setActivePageState] = useState(getPageFromPath(location.pathname));
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);

  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [showAdminIcon, setShowAdminIcon] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const contentRef = useRef(null);
  const scrollPositionRef = useRef(0);

  const isMobile = window.innerWidth <= 768;

  const setActivePage = (page) => {
    setActivePageState(page);
    navigate(getPathFromPage(page));
    setIsMenuOpen(false);
  };

  const setScrollTop = (value) => {
    if (isMobile) {
      window.scrollTo({ top: value, behavior: "auto" });
    } else if (contentRef.current) {
      contentRef.current.scrollTop = value;
    }
  };

  const preloadImages = (imageUrls) => {
    if (!imageUrls || imageUrls.length === 0) return;
    const imagesToPreload = imageUrls.slice(0, 3);
    imagesToPreload.forEach((url) => {
      if (url && url !== "/assets/urszula_logo.png") {
        const img = new Image();
        img.src = url;
      }
    });
  };

  useEffect(() => {
    if (location.pathname.startsWith("/kancelaria/")) {
      const slug = location.pathname.split("/")[2];

      const item = items.find((item) => item.slug === slug);

      if (item) {
        const details = itemDetails[item.id] || item;

        setSelectedItem(details);
        setActivePageState("ItemDetails");
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      if (user && isUserAdmin(user)) {
        setIsAuthenticated(true);
        if (activePage === "Login") {
          setActivePage("Panel administratora");
        }
      } else if (user && !isUserAdmin(user)) {
        setIsAuthenticated(false);
        await auth.signOut();
        if (activePage === "Panel administratora" || activePage === "Login") {
          setActivePage("Aktualności");
        }
      } else {
        setIsAuthenticated(false);
      }
    });

    return () => unsubscribe();
  }, [activePage]);

  useEffect(() => {
    if (!selectedPost && contentRef.current) {
      contentRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [selectedPost]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activePage]);

  useEffect(() => {
    if (posts && posts.length > 0 && !initialLoading) {
      const imageUrls = posts
        .filter(post => post.picture && post.picture !== "/assets/urszula_logo.png")
        .map(post => post.picture);
      preloadImages(imageUrls);
    }
  }, [posts, initialLoading]);

  const handleMenuToggle = () => setIsMenuOpen(!isMenuOpen);

  const handleSelectPost = (post) => {
    if (contentRef.current) {
      scrollPositionRef.current = contentRef.current.scrollTop;
    }
    setSelectedPost(post);
  };

  const handleSelectItem = (item, slug) => {
  if (contentRef.current) {
    scrollPositionRef.current = contentRef.current.scrollTop;
  }

  setSelectedItem(item);
  setActivePageState("ItemDetails");

  navigate(`/kancelaria/${slug}`);
};

  const handleBackFromItemDetails = () => {
    setSelectedItem(null);
    setActivePageState("Kancelaria");
    navigate("/kancelaria");
  };

  const fetchPosts = async (loadMore = false) => {
    if (loadingMore) return;

    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setInitialLoading(true);
      }

      const limit = 4;

      const url = loadMore && cursor
        ? `${process.env.REACT_APP_API_URL}/api/posts?after=${cursor}&limit=${limit}`
        : `${process.env.REACT_APP_API_URL}/api/posts?limit=${limit}`;

      const res = await fetch(url);
      const data = await res.json();

      if (!data.posts) return;

      setPosts(prev =>
        loadMore ? [...prev, ...data.posts] : data.posts
      );

      setCursor(data.nextCursor || null);

    } catch (err) {
      console.error("Błąd pobierania postów:", err);
    } finally {
      if (loadMore) {
        setLoadingMore(false);
      } else {
        setInitialLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchPosts(false);
  }, []);

  useEffect(() => {
    if (!selectedPost) {
      setTimeout(() => {
        setScrollTop(scrollPositionRef.current);
      }, 0);
    }
  }, [selectedPost]);

  useEffect(() => {
    setScrollTop(0);
  }, [activePage]);

  const openAdminPanelLogin = () => {
    setActivePage("Login");
  };

  return (
    <div className="full-screen-div">

      <div
        className="header"
        onMouseEnter={() => setShowAdminIcon(true)}
        onMouseLeave={() => setShowAdminIcon(false)}
      >

        <button
          className="menu-button"
          onClick={handleMenuToggle}
          aria-label="Otwórz menu"
        >
          ☰
        </button>

        <h1 className="parish-name">
          {isMobile
            ? <>{parishStatus}<br />{patronName}<br />{parishCity}</>
            : `${parishStatus} ${patronName} ${parishCity}`
          }
        </h1>

        <img
          className="logo-bt"
          src="/assets/urszula_photo.png"
          alt="Logo"
        />

        {showAdminIcon && (
          <MdAdminPanelSettings
            className="admin-icon"
            onClick={openAdminPanelLogin}
            size={30}
            color="#D4C2AE"
            style={{ cursor: 'pointer', transition: 'all 0.3s' }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          />
        )}

      </div>

      <div className="main">

        <div className="photo"></div>

        <div className={`menu ${isMenuOpen ? "open" : ""}`} aria-hidden={!isMenuOpen}>
          <VerticalMenu
            activePage={activePage}
            setActivePage={setActivePage}
          />
        </div>

        <div ref={contentRef} className="content">

          {activePage === "Aktualności" && !selectedPost && (
            <>
              {initialLoading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <NewsSection posts={posts} onSelectPost={handleSelectPost} facebookUrl={fanpage} />

                  {loadingMore && (
                    <LoadingSpinner />
                  )}

                  {cursor && !loadingMore && (
                    <button
                      className="load-more-btn"
                      onClick={() => fetchPosts(true)}
                    >
                      Załaduj kolejne
                    </button>
                  )}
                </>
              )}
            </>
          )}

          {activePage === "Aktualności" && selectedPost && (
            <NewsDetails post={selectedPost} onBack={() => setSelectedPost(null)} />
          )}

          {activePage === "O Parafii" && <About />}
          {activePage === "Duszpasterze" && <Priests />}
          {activePage === "Grupy parafialne" && <Groups />}
          {activePage === "Św. Urszula" && <Patron />}
          {activePage === "Msze Święte" && <Liturgy />}
          {activePage === "Nabożeństwa" && <Chapel />}
          {activePage === "Kancelaria" && <Office onSelectItem={handleSelectItem} />}
      
          {activePage === "Ochrona dzieci" && <ChildrenProtection />}
          {activePage === "Kontakt" && <Contact />}
          {activePage === "Intencje" && <Intentions />}
          {activePage === "Ogłoszenia" && <Announcements />}
          {activePage === "Login" && (
            <Login
              onLoginSuccess={() => {
                setIsAuthenticated(true);
                setActivePage("Panel administratora");
              }}
            />
          )}

          {activePage === "Panel administratora" && (
            isAuthenticated ? (
              <AdminPanel
                onLogout={() => {
                  setIsAuthenticated(false);
                  setActivePage("Login");
                }}
              />
            ) : (
              <Login
                onLoginSuccess={() => {
                  setIsAuthenticated(true);
                  setActivePage("Panel administratora");
                }}
              />
            )
          )}

        </div>

      </div>

      {isMenuOpen && (
        <div
          className="overlay"
          onClick={() => setIsMenuOpen(false)}
        ></div>
      )}

    </div>
  );
};

export default ParafiaWizytowka;